import type { JSX } from 'solid-js';
import { A, useNavigate } from '@solidjs/router';
import { createSignal, createResource, onCleanup, Show, For } from 'solid-js';
import { getUser, logoutUser } from '@/features/auth/store/session.store';
import { toggleTheme, getCurrentTheme, type ThemeName } from '@/theme';
import { FloatingActionButton } from '@/shared/ui';
import { DowngradeBanner } from '@/features/billing/components/DowngradeBanner';
import { ComplianceGate } from '@/features/billing/components/ComplianceGate';
import {
  fetchSubscription,
  hasFeature,
} from '@/features/billing/store/subscription.store';
import type { FeatureFlag } from '@/features/billing/types/billing.types';
import {
  fetchStorehouses,
  getLockedStorehouseCount,
} from '@/shared/stores/storehouses.store';
import { isAdmin } from '@/shared/stores/permissions.store';
import { isDev } from '@/features/auth/store/session.store';
import { listSubscriptionRequests } from '@/features/dev/api/dev.api';
import { t } from '@/shared/i18n';
import { LanguageSwitcher } from '@/shared/components/LanguageSwitcher';
import KhoaiLogo from '@/shared/components/KhoaiLogo';

interface MainLayoutProps {
  children: JSX.Element;
}

// ============================================
// Nav item definitions — features that require a paid plan
// get a lock icon + badge when the feature is unavailable.
// ============================================

interface NavItem {
  labelKey: string;
  href: string;
  /** If set, the link is gated behind this feature flag. */
  feature?: FeatureFlag;
  /** If set, the link is unlocked when ANY of these features is enabled. */
  anyFeature?: FeatureFlag[];
  /** Badge text shown when the feature is locked (e.g. "Pro"). */
  requiredPlanLabel?: string;
}

const NAV_ITEMS: NavItem[] = [
  { labelKey: 'nav.inventory', href: '/inventory' },
  { labelKey: 'nav.dataImport', href: '/data-import' },
  { labelKey: 'nav.activity', href: '/activity' },
  { labelKey: 'nav.clients', href: '/clients' },
  { labelKey: 'nav.suppliers', href: '/suppliers' },
  {
    labelKey: 'nav.analytics',
    href: '/analytics',
    anyFeature: ['basicAnalytics', 'analyticsReports', 'advancedReports'],
  },
];

export function MainLayout(props: MainLayoutProps) {
  const user = () => getUser();
  const [isDropdownOpen, setIsDropdownOpen] = createSignal(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = createSignal(false);
  const [currentTheme, setCurrentTheme] =
    createSignal<ThemeName>(getCurrentTheme());

  const handleThemeToggle = () => {
    const newTheme = toggleTheme();
    setCurrentTheme(newTheme);
  };

  const handleLogout = async () => {
    // Navigate to /login FIRST, then clear session.
    //
    // Why: MainLayout lives inside the protected route tree. If we mutate
    // session signals first, isAuthenticated() flips false, ProtectedRoute's
    // <Show> swaps to its fallback, the router starts a transition, and
    // any in-flight reactive computation inside the protected tree (resources,
    // effects, fetchSubscription, etc.) tries to update DOM nodes that the
    // transition is concurrently removing — producing the
    // "insertBefore: node is not a child of this node" panic.
    //
    // Navigating first lets the router cleanly unmount the protected tree
    // before any signal flips. logoutUser() then safely clears state for
    // the already-unmounted tree.
    navigate('/login', { replace: true });
    await logoutUser();
  };

  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen());
  };

  // Close dropdown when clicking outside
  const handleClickOutside = (e: MouseEvent) => {
    const target = e.target as HTMLElement;
    if (!target.closest('.user-dropdown')) {
      setIsDropdownOpen(false);
    }
  };

  // Add click listener and clean up on component disposal
  document.addEventListener('click', handleClickOutside);
  onCleanup(() => document.removeEventListener('click', handleClickOutside));

  const navigate = useNavigate();

  // Fetch subscription data eagerly so ComplianceGate can check limits on any route
  fetchSubscription();

  // Poll pending subscription requests for dev users (every 60s)
  const [pendingPollTick, setPendingPollTick] = createSignal(0);
  if (isDev()) {
    const pollInterval = setInterval(
      () => setPendingPollTick((n) => n + 1),
      60_000
    );
    onCleanup(() => clearInterval(pollInterval));
  }
  const [pendingCountResource] = createResource(
    () => (isDev() ? pendingPollTick() : null),
    async () => {
      try {
        const reqs = await listSubscriptionRequests('pending');
        return reqs.length;
      } catch {
        return 0;
      }
    }
  );
  const pendingCount = () => pendingCountResource() ?? 0;

  // Fetch storehouses into global store
  fetchStorehouses();
  const lockedStorehouseCount = () => getLockedStorehouseCount();

  return (
    <div
      class="relative min-h-screen"
      classList={{
        'bg-gradient-to-b from-[#fdf0e8] via-[#fce8d5] to-[#f9dcc4]':
          currentTheme() === 'light',
        'bg-gradient-to-b from-[#1a1a1a] via-[#111111] to-[#0a0a0a]':
          currentTheme() === 'dark',
      }}
    >
      {/* Header */}
      <header class="sticky top-0 z-40 border-b border-border-subtle bg-bg-surface shadow-sm">
        <nav class="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div class="flex h-16 items-center justify-between">
            {/* Logo and primary nav */}
            <div class="flex items-center gap-8">
              <A href="/" class="text-xl">
                <KhoaiLogo class="text-xl" />
              </A>
              {/* Desktop Navigation */}
              <div class="hidden gap-1 md:flex">
                <For each={NAV_ITEMS}>
                  {(item) => {
                    const locked = () => {
                      if (item.anyFeature)
                        return !item.anyFeature.some((f) => hasFeature(f));
                      if (item.feature) return !hasFeature(item.feature);
                      return false;
                    };
                    return (
                      <A
                        href={locked() ? '/billing' : item.href}
                        class={`relative rounded-md px-3 py-2 text-sm font-medium ${
                          locked()
                            ? 'text-text-tertiary hover:bg-bg-hover hover:text-text-secondary'
                            : 'text-text-secondary hover:bg-bg-hover hover:text-text-primary'
                        }`}
                        activeClass={
                          locked()
                            ? ''
                            : 'bg-accent-primary-subtle text-accent-primary hover:bg-accent-primary-subtle'
                        }
                        title={
                          locked()
                            ? t('nav.requiresPlan', {
                                plan: item.requiredPlanLabel ?? '',
                              })
                            : undefined
                        }
                      >
                        <span class="flex items-center gap-1.5">
                          {t(item.labelKey)}
                          <Show when={locked()}>
                            {/* Lock icon */}
                            <svg
                              class="h-3.5 w-3.5 text-text-tertiary"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                stroke-width="2"
                                d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                              />
                            </svg>
                          </Show>
                        </span>
                      </A>
                    );
                  }}
                </For>
              </div>
            </div>
            <div class="flex items-center gap-3">
              {/* Mobile menu button */}
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen())}
                class="rounded-lg p-2 text-text-secondary transition-colors hover:bg-bg-hover focus:outline-none focus:ring-2 focus:ring-border-focus md:hidden"
                aria-label={t('nav.toggleMenu')}
              >
                <Show
                  when={isMobileMenuOpen()}
                  fallback={
                    <svg
                      class="h-6 w-6"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width={2}
                        d="M4 6h16M4 12h16M4 18h16"
                      />
                    </svg>
                  }
                >
                  <svg
                    class="h-6 w-6"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </Show>
              </button>

              {/* Language switcher */}
              <LanguageSwitcher />

              {/* Theme toggle button */}
              <button
                onClick={handleThemeToggle}
                class="rounded-lg p-2 text-text-secondary transition-colors hover:bg-bg-hover focus:outline-none focus:ring-2 focus:ring-border-focus"
                title={
                  currentTheme() === 'dark'
                    ? t('nav.switchToLight')
                    : t('nav.switchToDark')
                }
              >
                <Show
                  when={currentTheme() === 'dark'}
                  fallback={
                    <svg
                      class="h-5 w-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width={2}
                        d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z"
                      />
                    </svg>
                  }
                >
                  <svg
                    class="h-5 w-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width={2}
                      d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z"
                    />
                  </svg>
                </Show>
              </button>

              {user() && (
                <div class="user-dropdown relative">
                  <button
                    onClick={toggleDropdown}
                    class="flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium text-text-secondary hover:bg-bg-hover focus:outline-none focus:ring-2 focus:ring-border-focus"
                  >
                    <div class="flex items-center gap-2">
                      <div class="relative">
                        <div class="flex h-8 w-8 items-center justify-center rounded-full bg-accent-primary font-semibold text-text-inverse">
                          {user()!.name.charAt(0).toUpperCase()}
                        </div>
                        <Show when={pendingCount() > 0}>
                          <span class="absolute -right-1 -top-1 flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-accent-danger px-0.5 text-[10px] font-bold leading-none text-white">
                            {pendingCount() > 9 ? '9+' : pendingCount()}
                          </span>
                        </Show>
                      </div>
                      <span class="hidden sm:block">{user()!.name}</span>
                    </div>
                    <svg
                      class="h-4 w-4 transition-transform"
                      classList={{ 'rotate-180': isDropdownOpen() }}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </button>

                  {/* Dropdown menu */}
                  <Show when={isDropdownOpen()}>
                    <div class="absolute right-0 z-50 mt-2 w-52 rounded-md border border-border-default bg-bg-surface py-1 shadow-lg">
                      <A
                        href="/profile"
                        class="block px-4 py-2 text-sm text-text-primary hover:bg-bg-hover"
                        onClick={() => setIsDropdownOpen(false)}
                      >
                        <div class="flex items-center gap-2">
                          <svg
                            class="h-4 w-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              stroke-linecap="round"
                              stroke-linejoin="round"
                              stroke-width="2"
                              d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                            />
                          </svg>
                          {t('nav.profile')}
                        </div>
                      </A>
                      <A
                        href="/settings"
                        class="block px-4 py-2 text-sm text-text-primary hover:bg-bg-hover"
                        onClick={() => setIsDropdownOpen(false)}
                      >
                        <div class="flex items-center gap-2">
                          <svg
                            class="h-4 w-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              stroke-linecap="round"
                              stroke-linejoin="round"
                              stroke-width="2"
                              d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                            />
                          </svg>
                          {t('nav.businessSettings')}
                        </div>
                      </A>
                      <A
                        href="/team"
                        class="block px-4 py-2 text-sm text-text-primary hover:bg-bg-hover"
                        onClick={() => setIsDropdownOpen(false)}
                      >
                        <div class="flex items-center gap-2">
                          <svg
                            class="h-4 w-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              stroke-linecap="round"
                              stroke-linejoin="round"
                              stroke-width="2"
                              d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                            />
                          </svg>
                          {t('nav.teamManagement')}
                        </div>
                      </A>
                      <A
                        href="/audit-log"
                        class="block px-4 py-2 text-sm text-text-primary hover:bg-bg-hover"
                        onClick={() => setIsDropdownOpen(false)}
                      >
                        <div class="flex items-center gap-2">
                          <svg
                            class="h-4 w-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              stroke-linecap="round"
                              stroke-linejoin="round"
                              stroke-width="2"
                              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                            />
                          </svg>
                          {t('nav.auditLog')}
                        </div>
                      </A>
                      <Show when={isAdmin()}>
                        <A
                          href="/billing"
                          class="block px-4 py-2 text-sm text-text-primary hover:bg-bg-hover"
                          onClick={() => setIsDropdownOpen(false)}
                        >
                          <div class="flex items-center gap-2">
                            <svg
                              class="h-4 w-4"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                stroke-width="2"
                                d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
                              />
                            </svg>
                            {t('nav.billing')}
                          </div>
                        </A>
                      </Show>
                      <Show when={isDev()}>
                        <div class="my-1 border-t border-border-subtle" />
                        <A
                          href="/dev"
                          class="block px-4 py-2 text-sm text-text-primary hover:bg-bg-hover"
                          onClick={() => setIsDropdownOpen(false)}
                        >
                          <div class="flex items-center gap-2">
                            <svg
                              class="h-4 w-4"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                stroke-width="2"
                                d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4"
                              />
                            </svg>
                            {t('nav.devPortal')}
                          </div>
                        </A>
                        <A
                          href="/dev/requests"
                          class="block px-4 py-2 text-sm text-text-primary hover:bg-bg-hover"
                          onClick={() => setIsDropdownOpen(false)}
                        >
                          <div class="flex items-center justify-between gap-2">
                            <div class="flex items-center gap-2">
                              <svg
                                class="h-4 w-4"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  stroke-linecap="round"
                                  stroke-linejoin="round"
                                  stroke-width="2"
                                  d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                                />
                              </svg>
                              {t('nav.requestsInbox')}
                            </div>
                            <Show when={pendingCount() > 0}>
                              <span class="flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-accent-danger px-1 text-[10px] font-bold text-white">
                                {pendingCount() > 99 ? '99+' : pendingCount()}
                              </span>
                            </Show>
                          </div>
                        </A>
                        <A
                          href="/dev/translations"
                          class="block px-4 py-2 text-sm text-text-primary hover:bg-bg-hover"
                          onClick={() => setIsDropdownOpen(false)}
                        >
                          <div class="flex items-center gap-2">
                            <svg
                              class="h-4 w-4"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                stroke-width="2"
                                d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129"
                              />
                            </svg>
                            {t('nav.translations')}
                          </div>
                        </A>
                      </Show>
                      <div class="my-1 border-t border-border-subtle"></div>
                      <button
                        onClick={handleLogout}
                        class="block w-full px-4 py-2 text-left text-sm text-text-primary hover:bg-bg-hover"
                      >
                        <div class="flex items-center gap-2">
                          <svg
                            class="h-4 w-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              stroke-linecap="round"
                              stroke-linejoin="round"
                              stroke-width="2"
                              d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                            />
                          </svg>
                          {t('nav.logout')}
                        </div>
                      </button>
                    </div>
                  </Show>
                </div>
              )}
            </div>
          </div>

          {/* Mobile Navigation Menu */}
          <Show when={isMobileMenuOpen()}>
            <div class="border-t border-border-subtle bg-bg-surface py-3 md:hidden">
              <div class="flex flex-col gap-1">
                <For each={NAV_ITEMS}>
                  {(item) => {
                    const locked = () => {
                      if (item.anyFeature)
                        return !item.anyFeature.some((f) => hasFeature(f));
                      if (item.feature) return !hasFeature(item.feature);
                      return false;
                    };
                    return (
                      <A
                        href={locked() ? '/billing' : item.href}
                        class={`block rounded-md px-3 py-2 text-sm font-medium ${
                          locked()
                            ? 'text-text-tertiary hover:bg-bg-hover hover:text-text-secondary'
                            : 'text-text-secondary hover:bg-bg-hover hover:text-text-primary'
                        }`}
                        activeClass={
                          locked()
                            ? ''
                            : 'bg-accent-primary-subtle text-accent-primary hover:bg-accent-primary-subtle'
                        }
                        onClick={() => setIsMobileMenuOpen(false)}
                        title={
                          locked()
                            ? t('nav.requiresPlan', {
                                plan: item.requiredPlanLabel ?? '',
                              })
                            : undefined
                        }
                      >
                        <span class="flex items-center gap-1.5">
                          {t(item.labelKey)}
                          <Show when={locked()}>
                            <svg
                              class="h-3.5 w-3.5 text-text-tertiary"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                stroke-width="2"
                                d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                              />
                            </svg>
                          </Show>
                        </span>
                      </A>
                    );
                  }}
                </For>
              </div>
            </div>
          </Show>
        </nav>
      </header>

      {/* Downgrade banner — locked storehouses only (pending plan change shown in /billing) */}
      <DowngradeBanner
        pendingDowngrade={null}
        lockedStorehouseCount={lockedStorehouseCount()}
        isAdmin={isAdmin()}
        onNavigateToBilling={() => navigate('/billing')}
      />

      {/* Main content */}
      <main class="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <ComplianceGate>{props.children}</ComplianceGate>
      </main>

      {/* Global Floating Action Button */}
      <FloatingActionButton />
    </div>
  );
}
